import type { DocSection } from '../types'

export const trackingSection: DocSection = {
  id: 'suivi',
  title: 'Rendez-vous & ventes (suivi)',
  icon: 'calendar',
  accent: '#D97706',
  summary: 'Retrouver et suivre les rendez-vous pris et les ventes conclues, avec filtres, statistiques et export.',
  minRole: 'agent',
  articles: [
    {
      id: 'rendez-vous',
      title: 'La page Rendez-vous',
      summary: 'Tous vos RDV au même endroit : filtres, statuts, export Excel.',
      icon: 'calendar',
      blocks: [
        { type: 'p', text: "La page **Rendez-vous** (barre latérale) liste les rendez-vous décrochés en fin d'appel. Un **Agent** voit **ses propres** rendez-vous (« Mes rendez-vous ») ; un **Superviseur** voit ceux de son équipe, avec des statistiques et des filtres supplémentaires (agent, équipe)." },
        { type: 'list', items: [
          "**Recherche** par client ou numéro, filtres par **campagne**, **statut**, et double plage de dates : date **du rendez-vous** ou date de **création**.",
          "Chaque rendez-vous porte un **statut** (à confirmer, validé, annulé…) que vous mettez à jour au fil du suivi.",
          "**Export Excel (.xlsx)** de la liste affichée, pour partager ou retravailler ailleurs.",
        ] },
        { type: 'callout', variant: 'info', title: 'Seuls les « validés » comptent', text: "Dans les statistiques et la page Performance, ce sont les rendez-vous **validés** qui sont comptés — pensez à mettre à jour le statut après confirmation du client." },
        { type: 'callout', variant: 'tip', title: 'Deux bases de statistiques (superviseurs)', text: "Le sélecteur **Stats : RDV pris / RDV planifiés** change la lecture : « pris » compte les RDV **créés** sur la période (production des agents), « planifiés » compte les RDV **prévus** sur la période (agenda à venir)." },
        { type: 'seealso', items: [
          { to: 'rappels/rdv', label: 'Comment prendre un rendez-vous en appel' },
          { to: 'dispositions/types', label: 'Les dispositions et leurs effets' },
        ] },
      ],
    },
    {
      id: 'ventes',
      title: 'La page Ventes',
      summary: 'Les ventes enregistrées en fin d\'appel, avec montants et chiffre d\'affaires.',
      icon: 'briefcase',
      blocks: [
        { type: 'p', text: "La page **Ventes** (barre latérale) liste les ventes enregistrées par les agents en fin d'appel (disposition de type **Vente**). Un **Agent** voit **ses propres** ventes ; un **Superviseur** voit celles de son périmètre, avec le **chiffre d'affaires** total et sa répartition **par campagne** et **par agent**." },
        { type: 'list', items: [
          "**Recherche** par client ou numéro, filtres par **campagne**, **agent** (superviseurs) et **période**.",
          "Le montant d'une vente provient du **formulaire de fin d'appel** de la campagne (champ montant).",
        ] },
        { type: 'callout', variant: 'tip', title: 'Enregistrement conservé', text: "Une disposition **Vente** protège automatiquement l'enregistrement de l'appel contre la purge — utile en cas de contestation. Voir [[historique/favoris|Favoris & rétention]]." },
        { type: 'seealso', items: [
          { to: 'dispositions/defaut', label: 'Les dispositions par défaut (dont Vente)' },
          { to: 'supervision/stats', label: 'Statistiques' },
        ] },
      ],
    },
    {
      id: 'recouvrement-fidelisation',
      title: "Suivi d'appels : Recouvrement & Fidélisation",
      summary: 'Passer en revue et valider les appels de recouvrement et de fidélisation.',
      icon: 'user-check',
      minRole: 'admin',
      blocks: [
        { type: 'p', text: "Quand un agent qualifie un appel **Recouvrement** ou **Fidélisation**, l'appel apparaît — en plus de l'historique — sur la page **Suivi d'appels** (barre latérale, section Admin). Un administrateur y passe chaque appel en revue et le **valide** : c'est la donnée validée qui fait foi pour le pilotage de ces opérations." },
        { type: 'h', text: 'Le circuit de validation' },
        { type: 'steps', items: [
          "Ouvrez **Suivi d'appels** et choisissez l'onglet **Recouvrement** ou **Fidélisation** (un badge indique le nombre d'appels à traiter).",
          "Filtrez par statut, campagne, agent, période — ou cherchez un client/numéro.",
          "**Valider** ou **Rejeter** chaque appel, avec une **note de revue** optionnelle (ex. « dossier transmis à la comptabilité »).",
          "Besoin de revenir dessus ? **À traiter** remet l'appel dans la file et efface la revue.",
        ] },
        { type: 'kv', items: [
          { k: 'À traiter', v: "L'appel attend une revue — c'est le filtre affiché par défaut." },
          { k: 'Validé', v: "Revu et conforme. Le suivi retient qui a validé, quand, et la note." },
          { k: 'Rejeté', v: "Revu et écarté (mauvaise qualification, doublon…) — tracé également." },
        ] },
        { type: 'callout', variant: 'info', title: 'Accès réservé', text: "La page est réservée aux **Administrateurs** et au **Propriétaire**. Les agents ne la voient pas ; leurs appels restent visibles dans leur propre historique." },
        { type: 'callout', variant: 'tip', title: 'Enregistrement conservé + réglage « Suivi »', text: "Ces appels gardent leur **enregistrement** (exempté de la purge) : réécoutez-le avant de statuer depuis l'[[historique|Historique]]. Le réglage **Suivi** (Recouvrement/Fidélisation/Sans suivi) s'ajuste sur n'importe quelle disposition dans **Paramètres de Disposition** de la campagne." },
      ],
    },
  ],
}
